import type { Entity, EntityInput, EntityType, ResourceType, SystemStage } from '../types/infrastructure'

const stageById: Record<string, SystemStage> = {
  brookfield: 'capital', bac: 'capital',
  blackstone: 'capital', softbank: 'capital', mgx: 'capital',
  microsoft: 'ai-cloud', amazon: 'ai-cloud', alphabet: 'ai-cloud', meta: 'ai-cloud', openai: 'ai-cloud', anthropic: 'ai-cloud',
  oracle: 'ai-cloud', xai: 'ai-cloud',
  nvidia: 'compute', smci: 'compute', 'gpu-fabric': 'compute',
  coreweave: 'compute',
  matador: 'data-center', paducah: 'data-center', 'savannah-river': 'data-center', 'northern-virginia': 'data-center',
  exelon: 'grid', pjm: 'grid', miso: 'grid',
  aep: 'grid',
  liberty: 'power', dominion: 'power', nextera: 'power',
  entergy: 'power', ppl: 'power',
  xenergy: 'nuclear', oklo: 'nuclear', seadrift: 'nuclear', 'aurora-inl': 'nuclear',
  constellation: 'nuclear', talen: 'nuclear', kairos: 'nuclear',
  haleu: 'fuel', uranium: 'fuel',
  fermi: 'buildout', amentum: 'buildout', dow: 'buildout', caterpillar: 'buildout', cooling: 'buildout', transformers: 'buildout', community: 'buildout',
  qts: 'data-center', equinix: 'data-center', 'digital-realty': 'data-center', vertiv: 'buildout', eaton: 'buildout', 'ge-vernova': 'buildout',
  doe: 'government', nrc: 'government',
  workforce: 'geography', 'illinois-cluster': 'geography',
}

const entityTypeById: Record<string, EntityType> = {
  pjm: 'grid-operator', miso: 'grid-operator', doe: 'government', nrc: 'government',
  matador: 'project', paducah: 'project', 'savannah-river': 'project', seadrift: 'project', 'aurora-inl': 'project',
  'northern-virginia': 'facility', workforce: 'region', 'illinois-cluster': 'region',
  haleu: 'commodity', uranium: 'commodity', community: 'community',
}

const resourceTypeById: Partial<Record<string, ResourceType>> = {
  fermi: 'construction', amentum: 'construction', dow: 'industrial-equipment', caterpillar: 'industrial-equipment',
  vertiv: 'cooling', eaton: 'industrial-equipment', 'ge-vernova': 'industrial-equipment',
  cooling: 'cooling', transformers: 'industrial-equipment', community: 'community', doe: 'policy-permitting', nrc: 'policy-permitting',
  workforce: 'workforce', pjm: 'transmission-interconnect', miso: 'transmission-interconnect',
}

const inferEntityType = (entity: EntityInput): EntityType => {
  if (entityTypeById[entity.id]) return entityTypeById[entity.id]
  if (entity.kind === 'company') return 'company'
  if (entity.kind === 'site') return entity.projectStatus ? 'project' : 'region'
  if (entity.kind === 'institution' && entity.status === 'Government') return 'government'
  return 'resource'
}

export const normalizeEntities = (entities: EntityInput[]): Entity[] => entities.map((entity) => ({
  ...entity,
  entityType: entity.entityType ?? inferEntityType(entity),
  systemStage: entity.systemStage ?? stageById[entity.id] ?? 'buildout',
  geography: entity.geography ?? { country: 'United States', state: entity.location?.state, city: entity.location?.city },
  marketTicker: entity.marketTicker ?? entity.ticker,
  resourceType: entity.resourceType ?? resourceTypeById[entity.id],
}))

export const primaryStages: SystemStage[] = ['capital', 'ai-cloud', 'compute', 'data-center', 'grid', 'power', 'nuclear', 'fuel']
export const buildoutResourceOrder: ResourceType[] = ['water', 'cooling', 'construction', 'industrial-equipment', 'land', 'workforce', 'community', 'policy-permitting', 'transmission-interconnect']

export const stageLabel: Record<SystemStage, string> = {
  capital: 'Capital', 'ai-cloud': 'AI / Cloud', compute: 'Compute', 'data-center': 'Data Centers', grid: 'Grid', power: 'Power', nuclear: 'Nuclear', fuel: 'Fuel', buildout: 'Buildout Input', government: 'Government', geography: 'Geography',
}

export const resourceLabel: Record<ResourceType, string> = {
  water: 'Water', cooling: 'Cooling', construction: 'Construction', 'industrial-equipment': 'Industrial Equipment', land: 'Land', workforce: 'Workforce', community: 'Community', 'policy-permitting': 'Policy & Permitting', 'transmission-interconnect': 'Transmission / Interconnect',
}
